import React, {
  useState,
  useMemo,
  useCallback,
  useContext,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { ThreeDots } from "react-loader-spinner";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Paper,
  Switch,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import RefreshIcon from "@material-ui/icons/Refresh";
import { toast } from "react-toastify";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import AuthContext from "connections/auth/AuthProvider.js";
import {
  updateVariantTags,
  updateInputDescription,
  getInputSanitizationRules,
  createInputSanitizationRule,
  updateInputSanitizationRule,
  deleteInputSanitizationRule,
  reprocessSanitization,
  getReprocessSanitizationProgress,
} from "views/Inputs/viewServices/apiCalls";
import TextEditor from "components/TextEditor/TextEditor";
import VariantTagCard from "./VariantTagCard";
import SanitizationRuleDialog from "./SanitizationRuleDialog";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  inputInfoSection: {
    marginBottom: theme.spacing(4),
  },
  inputHeader: {
    marginBottom: theme.spacing(2),
  },
  inputNameLabel: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: grayColor[2],
    textTransform: "uppercase",
    marginBottom: theme.spacing(0.5),
  },
  inputNameRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  inputName: {
    fontWeight: 500,
    fontSize: "1.25rem",
    color: grayColor[2],
  },
  inputIdChip: {
    fontSize: "0.7rem",
    fontWeight: 500,
    height: "20px",
    backgroundColor: grayColor[12],
    color: grayColor[3],
    borderRadius: "3px",
    "& .MuiChip-label": {
      padding: "0 8px",
    },
  },
  descriptionSection: {
    marginTop: theme.spacing(2),
  },
  descriptionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    width: "100%",
  },
  descriptionLabel: {
    fontSize: "0.75rem",
    fontWeight: 500,
    color: grayColor[2],
    textTransform: "uppercase",
  },
  inputFullDescription: {
    fontSize: "0.95rem",
    color: grayColor[2],
    lineHeight: 1.6,
    "& p": {
      margin: "0 0 8px 0",
    },
    "& p:last-child": {
      marginBottom: 0,
    },
  },
  emptyDescription: {
    fontSize: "0.95rem",
    color: grayColor[4],
    lineHeight: 1.6,
    fontStyle: "italic",
  },
  descriptionActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  saveButton: {
    backgroundColor: (props) => props.clientColor || "#1976d2",
    color: "#fff",
    "&:hover": {
      backgroundColor: (props) => props.clientColor || "#1565c0",
    },
  },
  title: {
    fontWeight: 500,
    fontSize: "0.75rem",
    marginBottom: theme.spacing(2),
    color: grayColor[2],
    textTransform: "uppercase",
  },
  titleNoMargin: {
    marginBottom: 0,
  },
  sectionTitleBlock: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    minHeight: 28,
    boxSizing: "border-box",
    backgroundColor: grayColor[12],
    borderRadius: 4,
    padding: theme.spacing(0.5, 1),
    marginBottom: theme.spacing(1.5),
  },
  headerActionButton: {
    width: 22,
    height: 22,
    padding: 0,
    marginLeft: theme.spacing(1),
    "& .MuiSvgIcon-root": {
      fontSize: "0.95rem",
    },
  },
  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2.25),
  },
  section: {
    marginTop: theme.spacing(4),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    width: "100%",
  },
  ruleList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  ruleCard: {
    padding: theme.spacing(1.5),
    border: `1px solid ${grayColor[12]}`,
    boxShadow: "none",
  },
  rulePhrase: {
    color: grayColor[2],
    fontSize: "0.9rem",
  },
  inactiveRule: {
    color: grayColor[4],
    fontStyle: "italic",
  },
  ruleActions: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  emptyState: {
    color: grayColor[4],
    fontStyle: "italic",
    padding: theme.spacing(4),
    textAlign: "center",
  },
  sectionLoading: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 96,
  },
  sectionError: {
    color: "#d32f2f",
    fontSize: "0.9rem",
    fontStyle: "italic",
  },
}));

const TabInputOptions = ({
  selectedInputTable,
  inputMetadata,
  allInputMetadata,
  inputColumns,
  allVariants,
  variants: initialVariants,
  isLoadingVariants,
  onVariantsChange,
  onMetadataChange,
}) => {
  const { userDetails } = useContext(AuthContext);
  const classes = useStyles({
    clientColor: userDetails?.implementation_color || "#1976d2",
  });
  const { t } = useTranslation("translation", { keyPrefix: "rgm.inputs" });

  // Local state for optimistic updates
  const [variants, setVariants] = useState(initialVariants);

  // Description editing state
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [sanitizationRules, setSanitizationRules] = useState([]);
  const [isLoadingSanitizationRules, setIsLoadingSanitizationRules] =
    useState(false);
  const [sanitizationRulesError, setSanitizationRulesError] = useState(null);
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [reprocessingRuleId, setReprocessingRuleId] = useState(null);

  // Update local state when props change
  useEffect(() => {
    setVariants(initialVariants);
  }, [initialVariants]);

  const fetchSanitizationRules = useCallback(async () => {
    setIsLoadingSanitizationRules(true);
    setSanitizationRulesError(null);
    setSanitizationRules([]);
    try {
      const response = await getInputSanitizationRules(selectedInputTable);
      setSanitizationRules(response || []);
    } catch (error) {
      const errorMessage =
        error.response?.data?.error || t("error_loading_sanitization_rules");
      setSanitizationRulesError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingSanitizationRules(false);
    }
  }, [selectedInputTable, t]);

  useEffect(() => {
    fetchSanitizationRules();
  }, [fetchSanitizationRules]);

  // Sort variants alphabetically by variant_name
  const sortedVariants = useMemo(() => {
    return [...variants].sort((a, b) => {
      const nameA = (a.variant_name || "").toLowerCase();
      const nameB = (b.variant_name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [variants]);

  // Compute available tags from all variants in this table
  const availableTags = useMemo(() => {
    const allTags = new Set();
    variants.forEach((v) => {
      if (v.tags) {
        v.tags.forEach((tag) => allTags.add(tag));
      }
    });
    return Array.from(allTags).sort();
  }, [variants]);

  // Optimistic update helper
  const updateVariantLocally = useCallback((variantId, newTags) => {
    setVariants((prev) =>
      prev.map((v) =>
        v.variant_id === variantId ? { ...v, tags: newTags } : v,
      ),
    );
  }, []);

  // Add tag to variant
  const handleAddTag = useCallback(
    async (variantId, tagName) => {
      const variant = variants.find((v) => v.variant_id === variantId);
      if (!variant) return;

      const currentTags = variant.tags || [];
      if (currentTags.includes(tagName)) return;

      const newTags = [...currentTags, tagName];

      // Optimistic update
      updateVariantLocally(variantId, newTags);

      try {
        await updateVariantTags(selectedInputTable, variantId, newTags);
        toast.success(t("tag_added"));

        // Notify parent of change
        if (onVariantsChange) {
          onVariantsChange();
        }
      } catch (error) {
        // Rollback on error
        updateVariantLocally(variantId, currentTags);
        toast.error(error.response?.data?.error || t("error_adding_tag"));
      }
    },
    [variants, selectedInputTable, t, updateVariantLocally, onVariantsChange],
  );

  // Remove tag from variant
  const handleRemoveTag = useCallback(
    async (variantId, tagName) => {
      const variant = variants.find((v) => v.variant_id === variantId);
      if (!variant) return;

      const currentTags = variant.tags || [];
      const newTags = currentTags.filter((t) => t !== tagName);

      // Optimistic update
      updateVariantLocally(variantId, newTags);

      try {
        await updateVariantTags(selectedInputTable, variantId, newTags);
        toast.success(t("tag_removed"));

        // Notify parent of change
        if (onVariantsChange) {
          onVariantsChange();
        }
      } catch (error) {
        // Rollback on error
        updateVariantLocally(variantId, currentTags);
        toast.error(error.response?.data?.error || t("error_removing_tag"));
      }
    },
    [variants, selectedInputTable, t, updateVariantLocally, onVariantsChange],
  );

  // Handle description edit
  const handleEditDescription = useCallback(() => {
    setEditedDescription(inputMetadata?.table_description_full || "");
    setIsEditingDescription(true);
  }, [inputMetadata]);

  const handleCancelDescription = useCallback(() => {
    setIsEditingDescription(false);
    setEditedDescription("");
  }, []);

  const handleSaveDescription = useCallback(async () => {
    setIsSavingDescription(true);
    try {
      await updateInputDescription(selectedInputTable, editedDescription);

      // Update parent state
      if (onMetadataChange) {
        onMetadataChange({
          ...inputMetadata,
          table_description_full: editedDescription,
        });
      }

      toast.success(t("description_updated_success"));
      setIsEditingDescription(false);
    } catch (error) {
      toast.error(error.response?.data?.error || t("description_update_error"));
    } finally {
      setIsSavingDescription(false);
    }
  }, [
    selectedInputTable,
    editedDescription,
    inputMetadata,
    onMetadataChange,
    t,
  ]);

  const getColumnLabel = useCallback(
    (tableId, columnId) => {
      return (
        inputColumns?.[tableId]?.columns?.[columnId]?.column_name ||
        columnId ||
        ""
      );
    },
    [inputColumns],
  );

  const getTableLabel = useCallback(
    (tableId) => {
      return (
        allInputMetadata?.[tableId]?.table_description ||
        allInputMetadata?.[tableId]?.table_name ||
        inputColumns?.[tableId]?.table_name ||
        tableId ||
        ""
      );
    },
    [allInputMetadata, inputColumns],
  );

  const getRulePhrase = useCallback(
    (rule) => {
      const config = rule.rule_config || {};
      const targetColumn = getColumnLabel(
        config.target_table || selectedInputTable,
        config.target_column,
      );
      const sourceTable = getTableLabel(config.source_table);
      const sourceColumn = getColumnLabel(
        config.source_table,
        config.source_column,
      );

      return t("sanitization_rule_phrase", {
        targetColumn,
        sourceColumn,
        sourceTable,
      });
    },
    [getColumnLabel, getTableLabel, selectedInputTable, t],
  );

  const handleOpenRuleDialog = useCallback((rule = null) => {
    setSelectedRule(rule);
    setIsRuleDialogOpen(true);
  }, []);

  const handleCloseRuleDialog = useCallback(() => {
    setSelectedRule(null);
    setIsRuleDialogOpen(false);
  }, []);

  const handleSaveRule = useCallback(
    async (payload) => {
      try {
        if (payload.rule_id) {
          await updateInputSanitizationRule(payload);
          toast.success(t("sanitization_rule_updated"));
        } else {
          await createInputSanitizationRule(payload);
          toast.success(t("sanitization_rule_created"));
        }
        handleCloseRuleDialog();
        fetchSanitizationRules();
      } catch (error) {
        toast.error(
          error.response?.data?.error || t("error_saving_sanitization_rule"),
        );
      }
    },
    [fetchSanitizationRules, handleCloseRuleDialog, t],
  );

  const handleReprocessRule = useCallback(
    async (rule) => {
      setReprocessingRuleId(rule.rule_id);
      // Background job + progress toast, so the user can leave the drawer.
      const toastId = toast.loading(t("reprocess_in_progress"));
      try {
        const { job_id: jobId } = await reprocessSanitization(
          selectedInputTable,
          rule.rule_id,
        );
        // Poll progress until done/failed; the work runs server-side even
        // if the user navigates away.
        let done = false;
        while (!done) {
          await new Promise((r) => setTimeout(r, 1500));
          const p = await getReprocessSanitizationProgress(jobId);
          if (p.status === "done") {
            done = true;
            toast.update(toastId, {
              render: t("sanitization_reprocessed", {
                count: p.variants_processed ?? 0,
              }),
              type: "success",
              isLoading: false,
              autoClose: 4000,
            });
          } else if (p.status === "failed") {
            done = true;
            toast.update(toastId, {
              render: p.error || t("error_reprocessing_sanitization"),
              type: "error",
              isLoading: false,
              autoClose: 5000,
            });
          }
        }
      } catch (error) {
        toast.update(toastId, {
          render:
            error.response?.data?.error || t("error_reprocessing_sanitization"),
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      } finally {
        setReprocessingRuleId(null);
      }
    },
    [selectedInputTable, t],
  );

  const handleToggleRule = useCallback(
    async (rule) => {
      try {
        await updateInputSanitizationRule({
          rule_id: rule.rule_id,
          is_active: !rule.is_active,
        });
        toast.success(
          rule.is_active
            ? t("sanitization_rule_disabled")
            : t("sanitization_rule_enabled"),
        );
        fetchSanitizationRules();
      } catch (error) {
        toast.error(
          error.response?.data?.error || t("error_saving_sanitization_rule"),
        );
      }
    },
    [fetchSanitizationRules, t],
  );

  const handleDeleteRule = useCallback(
    async (ruleId) => {
      try {
        await deleteInputSanitizationRule(ruleId);
        toast.success(t("sanitization_rule_deleted"));
        handleCloseRuleDialog();
        fetchSanitizationRules();
      } catch (error) {
        toast.error(
          error.response?.data?.error || t("error_saving_sanitization_rule"),
        );
      }
    },
    [fetchSanitizationRules, handleCloseRuleDialog, t],
  );

  const renderSectionLoading = () => (
    <Box className={classes.sectionLoading}>
      <ThreeDots
        color={userDetails?.implementation_color || "#1976d2"}
        height="32"
        width="56"
        radius="9"
        ariaLabel="three-dots-loading"
        visible={true}
      />
    </Box>
  );

  const renderVariantsSectionContent = () => {
    if (isLoadingVariants) {
      return renderSectionLoading();
    }

    if (!variants || variants.length === 0) {
      return (
        <Typography className={classes.emptyDescription}>
          {t("no_variants_available")}
        </Typography>
      );
    }

    return (
      <Box className={classes.grid}>
        {sortedVariants.map((variant) => (
          <VariantTagCard
            key={variant.variant_id}
            variant={variant}
            tableId={selectedInputTable}
            availableTags={availableTags}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            onVariantChange={onVariantsChange}
          />
        ))}
      </Box>
    );
  };

  const renderSanitizationSectionContent = () => {
    if (isLoadingSanitizationRules) {
      return renderSectionLoading();
    }

    if (sanitizationRulesError) {
      return (
        <Typography className={classes.sectionError}>
          {sanitizationRulesError}
        </Typography>
      );
    }

    if (sanitizationRules.length === 0) {
      return (
        <Typography className={classes.emptyDescription}>
          {t("no_sanitization_rules")}
        </Typography>
      );
    }

    return (
      <Box className={classes.ruleList}>
        {sanitizationRules.map((rule) => (
          <Paper key={rule.rule_id} className={classes.ruleCard}>
            <Box className={classes.inputNameRow}>
              <Typography
                className={`${classes.rulePhrase} ${
                  rule.is_active ? "" : classes.inactiveRule
                }`}
              >
                {getRulePhrase(rule)}
                {!rule.is_active && ` (${t("inactive")})`}
              </Typography>
              <Box className={classes.ruleActions}>
                <IconButton
                  size="small"
                  onClick={() => handleOpenRuleDialog(rule)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <Tooltip title={t("reprocess_sanitization")}>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleReprocessRule(rule)}
                      disabled={reprocessingRuleId !== null || !rule.is_active}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={rule.is_active ? t("disable") : t("enable")}>
                  <Switch
                    size="small"
                    checked={rule.is_active}
                    onChange={() => handleToggleRule(rule)}
                  />
                </Tooltip>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.inputInfoSection}>
        <Box className={classes.inputHeader}>
          <Box className={classes.sectionTitleBlock}>
            <Typography
              className={`${classes.inputNameLabel} ${classes.titleNoMargin}`}
            >
              {t("input_description")}
            </Typography>
            {!isEditingDescription && (
              <IconButton
                size="small"
                className={classes.headerActionButton}
                onClick={handleEditDescription}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          <Box className={classes.inputNameRow}>
            <Typography className={classes.inputName}>
              {inputMetadata?.table_description ||
                inputMetadata?.table_name ||
                selectedInputTable}
            </Typography>
            <Chip
              label={selectedInputTable}
              size="small"
              className={classes.inputIdChip}
            />
          </Box>
        </Box>
        <Box className={classes.descriptionSection}>
          {isEditingDescription ? (
            <>
              <TextEditor
                key={`description-${selectedInputTable}`}
                initialContent={editedDescription}
                readOnly={false}
                setContent={setEditedDescription}
              />
              <Box className={classes.descriptionActions}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCancelDescription}
                  disabled={isSavingDescription}
                >
                  {t("cancel")}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSaveDescription}
                  disabled={isSavingDescription}
                  className={classes.saveButton}
                >
                  {t("save_changes")}
                </Button>
              </Box>
            </>
          ) : inputMetadata?.table_description_full ? (
            <Box
              className={classes.inputFullDescription}
              dangerouslySetInnerHTML={{
                __html: inputMetadata.table_description_full,
              }}
            />
          ) : (
            <Typography className={classes.emptyDescription}>
              {t("no_description_available")}
            </Typography>
          )}
        </Box>
      </Box>

      <Box className={classes.sectionTitleBlock}>
        <Typography className={`${classes.title} ${classes.titleNoMargin}`}>
          {t("variants_already_uploaded")}
        </Typography>
      </Box>
      {renderVariantsSectionContent()}

      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Box className={classes.sectionTitleBlock}>
            <Typography className={`${classes.title} ${classes.titleNoMargin}`}>
              {t("additional_sanitization")}
            </Typography>
            <IconButton
              size="small"
              className={classes.headerActionButton}
              onClick={() => handleOpenRuleDialog()}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {renderSanitizationSectionContent()}
      </Box>

      <SanitizationRuleDialog
        open={isRuleDialogOpen}
        rule={selectedRule}
        selectedInputTable={selectedInputTable}
        inputColumns={inputColumns}
        allInputMetadata={allInputMetadata}
        allVariants={allVariants}
        clientColor={userDetails?.implementation_color || "#1976d2"}
        onClose={handleCloseRuleDialog}
        onSave={handleSaveRule}
        onDelete={handleDeleteRule}
      />
    </Box>
  );
};

TabInputOptions.propTypes = {
  selectedInputTable: PropTypes.string.isRequired,
  inputMetadata: PropTypes.object,
  allInputMetadata: PropTypes.object,
  inputColumns: PropTypes.object,
  allVariants: PropTypes.object,
  variants: PropTypes.array.isRequired,
  isLoadingVariants: PropTypes.bool,
  onVariantsChange: PropTypes.func,
  onMetadataChange: PropTypes.func,
};

export default TabInputOptions;
